import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  InputBase,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import { GenericTable } from '@components';
import useDashboardRequests from '@hooks/api/useDashboardRequests';
import useDebounce from '@hooks/useDebounce';
import { setToastMessage } from '@stores/App/slice';
import {
  SelfServeDataAccessStatus,
  TOAST_TYPES,
  DataAccessProvider,
  MMP_SOURCE_MAP,
  SelfServeDataAccessStatusMap,
} from '@constants/constants';
import { AdminRegisterUserPayload, useAdminRegisterUserMutation } from '@api/register';
import { Column, Row } from '../../../components/GenericTable/GenericTable';
import NewGameSidePanel from './components/NewGameSidePanel';
import { formatCurrency } from '@utils/common';
import Pagination from '../../Dashboard/components/Pagination';
import ModifyPermissionsDrawer from './ModifyPermissions';
import TableFilterDropdown from '../../../components/TableFilterDropdown';
import { useLazySelfServeCsvDownloadQuery } from '@api/selfServe';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import PortfolioDetailsDrawer from './PortfolioDetailsDrawer/PortfolioDetailsDrawer';
import NewPortfolioSidePanel from './components/Portfolio/NewPortfolioSidePanel';

type GameData = {
  dealName?: string;
  companyName: string;
  clientCode?: string;
  gameId: string;
  gameName: string;
  email: string;
  status: string;
  id: string;
  roasProjections: string;
  updatedAt: string;
  action?: JSX.Element;
  attachmentId?: string;
  monthlyMarketingSpends: string | null;
  mmp: DataAccessProvider | 'DIVERSIFIED';
  archived: boolean;
  type: string;
};

const columns: Column<GameData>[] = [
  { key: 'gameId', label: 'Game ID' },
  { key: 'gameName', label: 'Game Name' },
  { key: 'clientCode', label: 'Client ID' },
  { key: 'companyName', label: 'Client Name' },
  { key: 'dealName', label: 'Deal Name' },
  { key: 'email', label: 'Email ID' },
  {
    key: 'monthlyMarketingSpends',
    label: 'Monthly Marketing Spends',
  },
  { key: 'mmp', label: 'Data Source' },
  { key: 'status', label: 'Data Status' },
  { key: 'archived', label: 'Status' },
  { key: 'updatedAt', label: 'Time' },
];

const metadata = {
  chartType: 'TABLE',
  columns: [
    { id: 'gameId', type: 'string' },
    { id: 'gameName', type: 'string' },
    { id: 'clientCode', type: 'string' },
    { id: 'companyName', type: 'string' },
    { id: 'dealName', type: 'string' },
    { id: 'email', type: 'string' },
    { id: 'monthlyMarketingSpends', type: 'string' },
    { id: 'mmp', type: 'string' },
    { id: 'status', type: 'string' },
    { id: 'archived', type: 'string' },
    { id: 'updatedAt', type: 'string' },
    { id: 'action', type: 'action' },
  ],
};

const SELFSERVE_STATUS_COLORS: Record<string, string> = {
  NOT_REQUESTED: '#D3D3D3',
  PUBLISHED: '#5E8700',
  DATA_FETCH_FAILED: '#FF0000',
  FAILED: '#FF0000',
  READY_TO_PROCESS: '#FFA726',
  READY_TO_PUBLISH: '#FFA726',
  REQUESTED: '#FFA726',
  PENDING: '#FFA726',
  PENDING_DATA_PULL: '#FFA726',
  DATA_PULL_INITIATED: '#FFA726',

  PENDING_USER_CREATION: '#FFA726',
  PENDING_ACCESS: '#FFA726',
  CALL_SCHEDULED: '#FFA726',
  ACCESS_GRANTED: '#FFA726',
  DATA_FETCH_SUCCESSFUL: '#FFA726',
  DASHBOARD_CREATION_FAILED: '#FF0000',
};

const DASHBOARD_STATUS_COLORS: Record<string, string> = {
  REQUESTABLE: '#FFA726',
  REQUESTED: '#5E8700',
  NO_DATA: '#D3D3D3',
};

enum DashboardStatus {
  REQUESTABLE = 'Requestable',
  NOT_REQUESTED = 'Not Requested',
  REQUESTED = 'Requested',
  PENDING = 'Pending',
  DATA_FETCH_FAILED = 'Data Fetch Failed',
  READY_TO_PROCESS = 'Ready to Process',
  PENDING_DATA_PULL = 'Pending Data Pull',
  DATA_PULL_INITIATED = 'Data Pull Initiated',
  FAILED = 'Failed',
  READY_TO_PUBLISH = 'Ready to Publish',
  PUBLISHED = 'Published',
}

const statusOptions = Object.entries(SelfServeDataAccessStatusMap).map(
  ([value, label]) => ({
    label,
    value,
  }),
  ``,
);

const dataSourceOptions = Object.entries(MMP_SOURCE_MAP).map(([value, label]) => ({
  label,
  value,
}));

const PAGE_SIZE = 100;

const DashboardRequest = () => {
  const theme = useTheme();
  const isMediumView = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useDispatch();

  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const [search, setSearch] = useState<string>('');
  const [showDashboardRequestsSidePanel, setShowDashboardRequestsSidePanel] = useState(false);
  const [showNewGameSidePanel, setShowNewGameSidePanel] = useState(false);
  const [showNewPortfolioSidePanel, setShowNewPortfolioSidePanel] = useState(false);
  const [selectedRow, setSelectedRow] = useState<GameData | null>(null);
  const [dashboardData, setDashboardData] = useState<Row<GameData>[] | null>(null);
  const [selectedStatusFilters, setSelectedStatusFilters] = useState<string[]>([]);
  const [selectedDataSourceFilters, setSelectedDataSourceFilters] = useState<string[]>([]);
  const [ssrPagination, setSSRPagination] = useState<{
    totalSize: number;
    pageNumber: number;
  }>({ totalSize: 0, pageNumber: 0 });
  const [showArchived, setShowArchived] = useState<boolean>(false);
  const [showPortfolio, setShowPortfolio] = useState<boolean>(false);

  const { getDashboardRequestsData } = useDashboardRequests();
  const [adminRegisterUserApi, { isLoading: isLoadingAdminRegisterUserApi }] =
    useAdminRegisterUserMutation();
  const [selfServeCsvDownloadApi] = useLazySelfServeCsvDownloadQuery();

  useEffect(() => {
    fetchDashboardRequestData();
  }, []);

  const fetchDashboardRequestData = async (
    inputProps: {
      pageNumber?: number;
      search?: string;
      archived?: boolean;
      portfolio?: boolean;
      selectedStatuses?: string[];
      selectedDataSources?: string[];
    } = {},
  ) => {
    const args = {
      pageNumber: ssrPagination.pageNumber,
      pageSize: PAGE_SIZE,
      search: search,
      archived: showArchived,
      portfolio: showPortfolio,
      selectedStatuses: selectedStatusFilters,
      selectedDataSources: selectedDataSourceFilters,
      ...inputProps,
    };

    try {
      const response = await getDashboardRequestsData(
        args.search,
        args.pageNumber,
        args.pageSize,
        args.archived,
        args.portfolio,
        args.selectedStatuses,
        args.selectedDataSources,
      );

      if (response.items?.length > 0) {
        const formattedData = response.items.map((data: any) => {
          const financialProjection = data.requestedDashboards.find(
            (dashboard: any) => dashboard.moduleName === 'Financial Projections',
          );

          return {
            ...data,
            roasProjections:
              data.requestedDashboards?.length > 0 ? financialProjection.status : 'No Data',
          };
        });

        setDashboardData(formattedData);
        setSSRPagination({
          ...ssrPagination,
          totalSize: response.totalElements,
          pageNumber: args.pageNumber,
        });
      } else {
        setDashboardData([]);
        setSSRPagination({
          totalSize: 0,
          pageNumber: 0,
        });
      }
    } catch (error) {
      console.error(error);
      dispatch(
        setToastMessage({
          type: TOAST_TYPES.ERROR,
          message: 'Failed to fetch dashboard requests data',
        }),
      );
    }
  };

  const debouncedSearch = useDebounce(
    (value: string) =>
      fetchDashboardRequestData({
        search: value,
        pageNumber: 0,
      }),
    800,
  );

  const handleSearchFocus = () => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  const handleSelectedStatusFilters = (selected: string[]) => {
    setSelectedStatusFilters(selected);
    fetchDashboardRequestData({
      selectedStatuses: selected,
      pageNumber: 0,
    });
  };

  const handleSelectedDataSourceFilters = (selected: string[]) => {
    setSelectedDataSourceFilters(selected);
    fetchDashboardRequestData({
      selectedDataSources: selected,
      pageNumber: 0,
    });
  };

  const handleEditClick = (rowId: string) => {
    if (dashboardData) {
      const rowData = dashboardData?.find((row: any) => row.id === rowId);
      if (rowData) {
        setSelectedRow(rowData);
        setShowDashboardRequestsSidePanel(true);
      }
    }
  };

  const handleSearch = (value: string) => {
    setSearch(value);
    debouncedSearch(value);
  };

  const handleReload = () => {
    setSearch('');
    setShowArchived(false);
    setSelectedStatusFilters([]);
    setSelectedDataSourceFilters([]);
    fetchDashboardRequestData({
      pageNumber: 0,
      archived: false,
      selectedStatuses: [],
      selectedDataSources: [],
    });
  };

  const conditionallyMappedColumn = columns.map((col: any) => {
    if (col.key === 'gameId') {
      return {
        ...col,
        renderHeader: (column: Column<GameData>) => {
          return (
            <Typography
              sx={{
                fontFamily: 'Manrope, sans-serif',
                fontSize: '0.75rem',
                fontWeight: 700,
                color: '#F6F8F9',
                whiteSpace: 'nowrap',
              }}
            >
              {column.label}
            </Typography>
          );
        },
      };
    }
    if (col.key === 'dealName') {
      return {
        ...col,
        render: (key: string, row: any) => {
          const value = row.dealName || row.companyName;
          return (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography
                sx={{
                  fontFamily: 'Manrope, sans-serif',
                  fontWeight: 500,
                  fontSize: '0.875rem',
                  color: '#1B1C17',
                }}
              >
                {value}
              </Typography>
            </Box>
          );
        },
      };
    }
    if (col.key === 'status') {
      return {
        ...col,
        render: (key: string, row: any) => {
          const value = row[col.key];
          return (
            <Box
              sx={{
                display: 'flex',
                gap: '0.25rem',
                alignItems: 'center',
              }}
            >
              {value !== 'NOT_REQUESTED' && (
                <Box
                  sx={{
                    width: '0.75rem',
                    minWidth: '0.75rem',
                    height: '0.75rem',
                    borderRadius: '0.25rem',
                    backgroundColor: SELFSERVE_STATUS_COLORS[value] || '#D3D3D3',
                    marginRight: '0.5rem',
                  }}
                />
              )}
              <Typography
                sx={{
                  fontFamily: 'Manrope, sans-serif',
                  fontWeight: 500,
                  fontSize: '0.875rem',
                  color: '#1B1C17',
                }}
              >
                {SelfServeDataAccessStatusMap[value as SelfServeDataAccessStatus]}
              </Typography>
            </Box>
          );
        },
        renderHeader: (column: Column<GameData>) => {
          return (
            <Stack
              direction={'row'}
              alignItems={'center'}
              justifyContent={'space-between'}
              gap={'0.5rem'}
            >
              <span>{column.label}</span>
              <TableFilterDropdown
                setSelected={handleSelectedStatusFilters}
                selected={selectedStatusFilters}
                options={statusOptions}
              />
            </Stack>
          );
        },
      };
    }
    if (col.key === 'archived') {
      return {
        ...col,
        render: (key: string, row: any) => {
          return (
            <Typography
              sx={{
                fontFamily: 'Manrope, sans-serif',
                fontWeight: 500,
                fontSize: '0.875rem',
                color: '#1B1C17',
              }}
            >
              {row.archived ? 'Archived' : '-'}
            </Typography>
          );
        },
      };
    }
    if (col.key === 'roasProjections') {
      return {
        ...col,
        render: (key: string, row: any) => {
          const value = row[col.key];
          return (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {value !== 'NOT_REQUESTED' && (
                <Box
                  sx={{
                    width: '0.75rem',
                    minWidth: '0.75rem',
                    height: '0.75rem',
                    borderRadius: '0.25rem',
                    backgroundColor: DASHBOARD_STATUS_COLORS[value] || '#D3D3D3',
                    marginRight: '0.5rem',
                  }}
                />
              )}
              <Typography
                sx={{
                  fontFamily: 'Manrope, sans-serif',
                  fontWeight: 500,
                  fontSize: '0.875rem',
                  color: '#1B1C17',
                }}
              >
                {DashboardStatus[value as keyof typeof DashboardStatus] || 'No Data'}
              </Typography>
            </Box>
          );
        },
      };
    }
    if (col.key === 'updatedAt') {
      return {
        ...col,
        render: (key: string, row: any) => {
          const value = row[col.key];
          return (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography
                sx={{
                  fontFamily: 'Manrope, sans-serif',
                  fontWeight: 500,
                  fontSize: '0.875rem',
                  color: '#1B1C17',
                }}
              >
                {new Date(value).toUTCString()}
              </Typography>
            </Box>
          );
        },
      };
    }
    if (col.key === 'monthlyMarketingSpends') {
      return {
        ...col,
        render: (key: string, row: any) => {
          const columnValue = row[col.key];

          // Return a default placeholder if the column value is missing
          if (!columnValue) {
            return <Box>-</Box>;
          }

          const rangeParts = columnValue.split(' - ');
          let formattedValue = '';

          if (rangeParts.length === 2) {
            // Handle range values (e.g., "$1,000 - $2,000")
            const [from, to] = rangeParts;
            const formattedFrom = formatCurrency(from.slice(1).replaceAll(',', ''), true);
            const formattedTo = formatCurrency(to.slice(1).replaceAll(',', ''), true);
            formattedValue = `${formattedFrom} - ${formattedTo}`;
          } else if (rangeParts.length === 1) {
            // Handle single currency values (e.g., "> $1,000")
            const [currencySymbol, amount] = rangeParts[0].split(' ');
            const formattedAmount = formatCurrency(amount.slice(1).replaceAll(',', ''), true);
            formattedValue = `${currencySymbol} ${formattedAmount}`;
          }

          return <Box sx={{ textWrap: 'nowrap' }}>{formattedValue}</Box>;
        },
      };
    }
    if (col.key === 'mmp') {
      return {
        ...col,
        render: (key: string, row: any) => {
          const value = row[col.key];
          return (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography
                sx={{
                  fontFamily: 'Manrope, sans-serif',
                  fontWeight: 500,
                  fontSize: '0.875rem',
                  color: '#1B1C17',
                }}
              >
                {MMP_SOURCE_MAP[value as DataAccessProvider] || '-'}
              </Typography>
            </Box>
          );
        },
        renderHeader: (column: Column<GameData>) => {
          return (
            <Stack
              direction={'row'}
              alignItems={'center'}
              justifyContent={'space-between'}
              gap={'0.5rem'}
            >
              <span>{column.label}</span>
              <TableFilterDropdown
                setSelected={handleSelectedDataSourceFilters}
                selected={selectedDataSourceFilters}
                options={dataSourceOptions}
              />
            </Stack>
          );
        },
      };
    }
    return col;
  });

  const cleanPayload = ({
    playStoreUrl,
    appStoreUrl,
    accountName,
    iapMargin,
    ...rest
  }: AdminRegisterUserPayload) => {
    const cleanedPayload: any = { ...rest };

    if (iapMargin?.trim() === '') {
      cleanedPayload.iapMargin = null;
    } else if (iapMargin !== undefined) {
      cleanedPayload.iapMargin = iapMargin;
    }

    if (accountName?.trim() === '') {
      cleanedPayload.accountName = null;
    } else if (accountName !== undefined) {
      cleanedPayload.accountName = accountName;
    }

    if (playStoreUrl?.trim() !== '') {
      cleanedPayload.playStoreUrl = playStoreUrl;
    }

    if (appStoreUrl?.trim() !== '') {
      cleanedPayload.appStoreUrl = appStoreUrl;
    }

    return cleanedPayload;
  };

  const handleCreateNewGame = async (payload: AdminRegisterUserPayload) => {
    try {
      const processedPayload = cleanPayload(payload);
      const res = await adminRegisterUserApi(processedPayload).unwrap();

      return res;
    } catch (error: any) {
      const { data, detailedErrorObject } = error;

      if (detailedErrorObject?.code === 4052) {
        dispatch(
          setToastMessage({
            type: TOAST_TYPES.ERROR,
            heading: 'Adjust Account Name Not Found!',
            message:
              "Please ensure that access was granted to underwriting@pvxpartner.com and that your account name exactly matches your Adjust account. If you've already done this, please try syncing again or reach us at info@pvxpartners.com.",
          }),
        );
      } else if (detailedErrorObject?.code === 4053) {
        dispatch(
          setToastMessage({
            type: TOAST_TYPES.ERROR,
            heading: 'Game IDs Not Found!',
            message:
              "The game IDs you shared aren't linked to the Adjust account. Please verify app access.",
          }),
        );
      } else {
        // General error handling

        dispatch(
          setToastMessage({
            type: TOAST_TYPES.ERROR,
            message: data?.message || 'Unable to register user, please try again later.',
          }),
        );
      }
    }
  };

  const handleCreateSuccess = () => {
    setShowNewGameSidePanel(false);
    setShowNewPortfolioSidePanel(false);
    handleReload();
  };

  const handleToggleArchived = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = e.target;
    setShowArchived(checked);
    fetchDashboardRequestData({
      archived: checked,
      pageNumber: 0,
    });
  };

  const handleTogglePortfolios = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = e.target;
    setShowPortfolio(checked);
    fetchDashboardRequestData({
      portfolio: checked,
      pageNumber: 0,
    });
  };

  const handlePageChange = (newPage: number) => {
    fetchDashboardRequestData({
      pageNumber: newPage - 1,
    });
  };

  const handleDownload = async () => {
    try {
      const params = new URLSearchParams();

      selectedStatusFilters.forEach((status) => {
        params.append('status', status);
      });
      selectedDataSourceFilters.forEach((mmp) => {
        params.append('mmp', mmp);
      });
      const queryObject: {
        mmp: string[];
        status: string[];
        showArchived: boolean;
        portfolio: boolean;
      } = {
        status: selectedStatusFilters,
        mmp: selectedDataSourceFilters,
        showArchived: showArchived,
        portfolio: showPortfolio,
      };

      const response = await selfServeCsvDownloadApi(queryObject).unwrap();

      if (response) {
        const link = document.createElement('a');
        link.href = response.data || '';
        link.download = 'dashboard_requests.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      dispatch(
        setToastMessage({
          type: TOAST_TYPES.ERROR,
          message: 'Unable to download CSV, please try again later.',
        }),
      );
    }
  };

  return (
    <div
      style={{
        padding: '1rem 2rem',
        paddingBottom: isMediumView ? '4rem' : '2rem',
        overflow: 'scroll',
        maxHeight: '100%',
      }}
    >
      <Box
        id="header_wrapper"
        sx={{
          mb: '2rem',
          mt: '1rem',
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: 600,
            fontSize: '2rem',
            lineHeight: 1.5,
            fontFamliy: 'Manrope, sans-serif',
            marginTop: '0.5rem',
          }}
        >
          Self Serve Requests
        </Typography>
      </Box>
      <Box
        sx={{
          marginBottom: '1.5rem',
          display: 'flex',
          gap: '1rem',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Paper
          component="form"
          sx={{
            height: 45,
            px: '19px',
            display: 'flex',
            alignItems: 'center',
            borderRadius: '28px',
            mb: '10px',
            backgroundColor: theme.palette.custom_field_background,
            width: isMediumView ? '100%' : '60%',
            boxShadow: 'none',
          }}
        >
          <InputBase
            inputRef={searchInputRef}
            placeholder={
              isMediumView ? `Search ...` : `Search Email / Game ID / Game Name / Client Name`
            }
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
              }
            }}
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flex: 1,
            }}
          />
          {search ? (
            <Box sx={{ mt: 1, cursor: 'pointer' }} onClick={() => handleSearch('')}>
              <CloseIcon />
            </Box>
          ) : (
            <Box sx={{ mt: 1, cursor: 'pointer' }} onClick={handleSearchFocus}>
              <SearchIcon />
            </Box>
          )}
        </Paper>
        <Box sx={{ display: 'flex', gap: '1rem' }}>
          <Button
            variant="contained"
            onClick={() => setShowNewPortfolioSidePanel(true)}
            sx={{
              borderRadius: '100px',
              fontWeight: '700',
              textTransform: 'none',
              backgroundColor: 'transparent',
              border: '1px solid #2E4A4F',
              color: '#6C6C6C',
              fontFamily: 'Manrope, sans-serif',
              '&:hover': {
                backgroundColor: 'transparent',
              },
              padding: '10px 24px',
              boxShadow: 'none',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '0.25rem',
              }}
            >
              <AddIcon
                htmlColor={theme.palette.primary.main}
                sx={{
                  height: '1rem',
                  width: '1rem',
                  cursor: 'pointer',
                  backgroundColor: 'transparent',
                }}
              />
              <Typography
                sx={{
                  color: '#6C6C6C',
                  fontSize: 14,
                  fontWeight: 700,
                }}
              >
                Portfolio
              </Typography>
            </Box>
          </Button>
          <Button
            variant="contained"
            onClick={() => setShowNewGameSidePanel(true)}
            sx={{
              borderRadius: '100px',
              fontWeight: '700',
              textTransform: 'none',
              backgroundColor: theme.palette.primary.main,
              color: '#F6F8F9',
              fontFamily: 'Manrope, sans-serif',
              '&:hover': {
                backgroundColor: theme.palette.primary.main,
              },
              padding: '10px 24px',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '0.25rem',
              }}
            >
              <AddIcon
                htmlColor={theme.palette.common.white}
                sx={{
                  height: '1rem',
                  width: '1rem',
                  cursor: 'pointer',
                  backgroundColor: 'transparent',
                }}
              />
              <Typography
                sx={{
                  color: theme.palette.common.white,
                  fontSize: 14,
                  fontWeight: 700,
                }}
              >
                New Game
              </Typography>
            </Box>
          </Button>
        </Box>
      </Box>
      <Box
        sx={{
          width: '100%',
          backgroundColor: '#FFFFFF',
          padding: '1rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
          borderRadius: '0.5rem',
        }}
      >
        <Stack
          direction={isMediumView ? 'column' : 'row'}
          justifyContent={'space-between'}
          alignItems={'center'}
        >
          <Typography
            sx={{
              fontFamily: 'Manrope, sans-serif',
              fontSize: '1rem',
              fontWeight: 700,
            }}
          >
            Self Serve Requests
          </Typography>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '1rem',
            }}
          >
            {ssrPagination.totalSize > 0 && (
              <Pagination
                page={ssrPagination.pageNumber + 1}
                size={PAGE_SIZE}
                total={ssrPagination.totalSize}
                handleChange={handlePageChange}
                fileName={''}
                chartId={''}
                submoduleId={''}
                filterSelection={{}}
              />
            )}
            <IconButton
              onClick={handleDownload}
              sx={{
                color: '#2E736E',
                padding: '4px',
              }}
            >
              <FileDownloadOutlinedIcon />
            </IconButton>
            <IconButton
              onClick={handleReload}
              sx={{
                color: '#2E736E',
                padding: '4px',
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Box>
        </Stack>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: '1rem',
            justifyContent: 'flex-end',
          }}
        >
          <Stack direction={'row'} alignItems={'center'} gap={'0.5rem'}>
            <Typography
              fontSize={'0.875rem'}
              lineHeight={'1.25rem'}
              fontWeight={500}
              color={'#000000'}
            >
              Show Portfolios
            </Typography>
            <Checkbox
              disableFocusRipple
              disableRipple
              disableTouchRipple
              checked={showPortfolio}
              onChange={handleTogglePortfolios}
              sx={{
                p: 0,
              }}
            />
          </Stack>
          <Stack direction={'row'} alignItems={'center'} gap={'0.5rem'}>
            <Typography
              fontSize={'0.875rem'}
              lineHeight={'1.25rem'}
              fontWeight={500}
              color={'#000000'}
            >
              Show Archived
            </Typography>
            <Checkbox
              disableFocusRipple
              disableRipple
              disableTouchRipple
              checked={showArchived}
              onChange={handleToggleArchived}
              sx={{
                p: 0,
              }}
            />
          </Stack>
        </Box>
        {dashboardData && (
          <GenericTable
            data={dashboardData}
            columns={conditionallyMappedColumn}
            meta={metadata}
            actions={[
              {
                label: 'Edit',
                action: handleEditClick,
              },
            ]}
          />
        )}
      </Box>
      {showNewGameSidePanel && (
        <NewGameSidePanel
          showNewGameSidePanel={true}
          setShowNewGameSidePanel={setShowNewGameSidePanel}
          createNewGameAction={handleCreateNewGame}
          onCreateSuccess={handleCreateSuccess}
          isLoading={isLoadingAdminRegisterUserApi}
        />
      )}
      {showNewPortfolioSidePanel && (
        <NewPortfolioSidePanel
          showNewPortfolioSidePanel={showNewPortfolioSidePanel}
          setShowNewPortfolioSidePanel={setShowNewPortfolioSidePanel}
          onCreateSuccess={handleCreateSuccess}
        />
      )}
      {selectedRow &&
        (selectedRow.type === 'PORTFOLIO' ? (
          <PortfolioDetailsDrawer
            show={showDashboardRequestsSidePanel}
            close={(change: boolean, reset: boolean) => {
              setSelectedRow(null);
              const args: Record<string, number | string> = {};
              if (reset) {
                args['pageNumber'] = 0;
              }
              setShowDashboardRequestsSidePanel(false);
              fetchDashboardRequestData(args);
            }}
            portfolioId={selectedRow?.id}
          />
        ) : (
          <ModifyPermissionsDrawer
            show={showDashboardRequestsSidePanel}
            close={(change: boolean, reset: boolean) => {
              setSelectedRow(null);
              const args: Record<string, number | string> = {};
              if (reset) {
                args['pageNumber'] = 0;
              }
              setShowDashboardRequestsSidePanel(false);
              fetchDashboardRequestData(args);
            }}
            id={selectedRow?.id}
          />
        ))}
    </div>
  );
};

export default DashboardRequest;
